import { useMemo } from 'react';

import clsx from 'clsx';

import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
// Hooks
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';

import PropTypes from 'prop-types';

import { aclFeatures } from '@/lib/acl/aclFeatures';
// Lib
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    TIKTOK_BUSINESS,
    TWITTER,
    YOUTUBE
} from '@/lib/socialProfile';

import Icon from '@/components/base/Icon';
import FacebookPost from '@/components/base/Post/FacebookPost';
import InstagramPost from '@/components/base/Post/InstagramPost';
import LinkedinPost from '@/components/base/Post/LinkedinPost';
import TiktokPost from '@/components/base/Post/TiktokPost';
import TwitterPost from '@/components/base/Post/TwitterPost';
import YoutubePost from '@/components/base/Post/YoutubePost';

// Components
import { BarGroupedChartComponent } from '../BarGroupedChart';
import ChartWrapper from '../ChartWrapper';

const buttonNextPrevClass =
    'w-10 h-10 flex items-center justify-center text-icoGray-500 border-2 border-icoGray-300 rounded-full dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf dark:border-0 disabled:opacity-50 disabled:pointer-events-none';

const CarouselPostLifespan = ({ data, setRequestParams, setState, isDashboard, isExport }) => {
    const { t } = useTranslation();
    const { hasAcl } = useAcl();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const network = useMemo(() => {
        if (data.social_profiles && !isExport) {
            const socialProfile = activatedSocialProfiles.find(
                (sp) => sp.id === data.social_profiles[0]
            );
            return socialProfile?.social_network_type;
        } else if (isExport) {
            return data.social_profile?.social_network_type;
        }

        return activeSocialProfile?.social_network_type ?? INSTAGRAM;
    }, [
        activatedSocialProfiles,
        activeSocialProfile?.social_network_type,
        data.social_profile?.social_network_type,
        data.social_profiles,
        isExport
    ]);

    const handleClickChangeMedia = (idMedia) => {
        if (!isDashboard) {
            setRequestParams((prev) => ({
                ...prev,
                parameters: {
                    ...prev.parameters,
                    media_life_span: {
                        ...prev.parameters?.media_life_span,
                        id_media: idMedia
                    }
                }
            }));
            setState((prev) => ({ ...prev, media_life_span: {} }));
        } else {
            setRequestParams({
                ...data,
                parameters: { ...data.parameters, id_media: idMedia }
            });
            setState((prev) => {
                let type = 'social_profiles';
                if (data.groups?.length > 0) {
                    type = 'groups';
                }

                return {
                    ...prev,
                    [type]: {
                        ...prev[type],
                        [data[type][0]]: {
                            ...prev[type][data[type][0]],
                            [data.id]: {}
                        }
                    }
                };
            });
        }
    };

    const renderPost = (media) => {
        // Disable promoted_post if the user doesn't have the ACL
        let promoted_post = false;
        if (isExport || hasAcl(aclFeatures.analytic.promoted_post.read)) {
            promoted_post = media?.promoted_post ?? false;
        }
        switch (network) {
            case LINKEDIN:
                return (
                    <LinkedinPost
                        data={{ ...media, promoted_post }}
                        isLoading={false}
                        isExport={isExport}
                        postConfig={{ hasHover: false, hasFooter: false }}
                    />
                );

            case TWITTER:
                return (
                    <TwitterPost
                        data={{ ...media, promoted_post }}
                        isLoading={false}
                        isExport={isExport}
                        postConfig={{ hasHover: false, hasFooter: false }}
                    />
                );

            case FACEBOOK:
                return (
                    <FacebookPost
                        data={{ ...media, promoted_post }}
                        isLoading={false}
                        isExport={isExport}
                        postConfig={{ hasHover: false, hasFooter: false }}
                    />
                );
            case TIKTOK_BUSINESS:
                return (
                    <TiktokPost
                        data={{ ...media, promoted_post }}
                        isMediaLifeSpan
                        isLoading={false}
                        isExport={isExport}
                        postConfig={{ hasHover: false, hasFooter: false }}
                    />
                );
            case YOUTUBE:
                return (
                    <YoutubePost
                        data={{ ...media, promoted_post }}
                        isMediaLifeSpan
                        isLoading={false}
                        isExport={isExport}
                        postConfig={{ hasHover: false, hasFooter: false }}
                    />
                );

            default:
                return (
                    <InstagramPost
                        data={{ ...media, promoted_post }}
                        isLoading={false}
                        isExport={isExport}
                        postConfig={{ hasHover: false, hasFooter: false }}
                    />
                );
        }
    };

    return (
        <div className="min-w-[16rem] lg:mr-8">
            <div className="flex justify-center">
                {data?.previous_media ? (
                    <div
                        className={clsx('relative rounded-lg opacity-50', {
                            'w-[100px]': network === TIKTOK_BUSINESS,
                            'h-[150px] w-[150px]': network !== TIKTOK_BUSINESS
                        })}>
                        {renderPost(data?.previous_media)}
                    </div>
                ) : null}
                {data?.media ? (
                    <div
                        className={clsx('relative z-1 mt-12 rounded-lg border-white', {
                            'w-[100px]': network === TIKTOK_BUSINESS,
                            'h-[150px] w-[150px] border-3': network !== TIKTOK_BUSINESS,
                            '-ml-12': data?.previous_media && network !== TIKTOK_BUSINESS,
                            '-ml-8': data?.previous_media && network === TIKTOK_BUSINESS,
                            'dark:border-icoDarkMode-anthracite': !isExport
                        })}>
                        {renderPost(data?.media)}
                    </div>
                ) : null}
            </div>

            {!isExport ? (
                <div className="mt-5 flex w-full justify-between">
                    <button
                        disabled={!data?.previous_media}
                        className={buttonNextPrevClass}
                        aria-label={t('commons:previous')}
                        onClick={() => handleClickChangeMedia(data?.previous_media?.id.toString())}>
                        <Icon icon="chevron-left" />
                    </button>
                    <button
                        disabled={!data?.next_media}
                        className={buttonNextPrevClass}
                        aria-label={t('commons:next')}
                        onClick={() => handleClickChangeMedia(data?.next_media?.id.toString())}>
                        <Icon icon="chevron-right" />
                    </button>
                </div>
            ) : null}
        </div>
    );
};

CarouselPostLifespan.propTypes = {
    data: PropTypes.object,
    setRequestParams: PropTypes.func,
    setState: PropTypes.func
};

const MediaLifeSpan = ({
    data,
    className = '',
    onReset = () => {
        /** */
    },
    isLoading = false,
    setRequestParams,
    setState,
    ...restProps
}) => {
    return (
        <ChartWrapper
            className={clsx(className, 'lg:justify-between lg:space-y-0')}
            isLoading={isLoading}
            onReset={onReset}
            data={data}
            setRequestParams={setRequestParams}
            setState={setState}
            legendType="square"
            {...restProps}>
            <CarouselPostLifespan setRequestParams={setRequestParams} setState={setState} />
            <BarGroupedChartComponent />
        </ChartWrapper>
    );
};

MediaLifeSpan.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool,
    setRequestParams: PropTypes.func,
    setState: PropTypes.func
};

export default MediaLifeSpan;
